import React from 'react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { MultiSelect, MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import { Card, CardBody } from '@progress/kendo-react-layout';
import { SvgIcon } from '@progress/kendo-react-common';
import { pencilIcon, trashIcon, userIcon } from '@progress/kendo-svg-icons';
import greaseBossMark from '../../../../../Assets/greaseboss-icon.png';
import type { EntityNoteCategory, Note } from './EntityNotesPanel';
import type { CategoryOption } from './noteHooks';
import NoteText from './NoteText';
import NoteDescriptionInput from './NoteDescriptionInput';
import useIsGbUser from '../../../../Identity/Utilities/useIsGbUser';

interface Props {
  note: Note;
  /** Identity content rendered top-left of the card (timestamp in the drawer, entity name on the timeline). */
  header: React.ReactNode;
  categories: CategoryOption[];
  categoryLabel: (cat: EntityNoteCategory) => string;
  /** Whether the current user owns this note — gates the edit/delete actions. */
  isOwn: boolean;
  /** Internal (GreaseBoss) author → GreaseBoss mark, otherwise the generic user icon. */
  authorIsInternal: boolean;

  // Inline edit state — supplied by `useNoteEditing` (or the drawer's own state).
  isBeingEdited: boolean;
  editingDescription: string;
  editingCategories: CategoryOption[];
  editingInternalOnly: boolean;
  onChangeEditingDescription: (v: string) => void;
  onChangeEditingCategories: (v: CategoryOption[]) => void;
  onChangeEditingInternalOnly: (v: boolean) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: () => void;

  renderCategoryItem: (li: React.ReactElement, itemProps: any) => React.ReactElement;
  labels: { createdBy: string; edit: string; delete: string; cancel: string; save: string };
  /** Extra classes for the outer Card (e.g. `shrink-0` when stacked in the drawer). */
  className?: string;
}

/**
 * A single note card: header slot + categories, body (read or edit), footer (author + actions).
 * Shared by the drawer panel and the timeline tabs — only the `header` slot and outer layout differ,
 * which each surface supplies. The body/edit/footer chrome is identical everywhere.
 */
export default function NoteCard({
  note,
  header,
  categories,
  categoryLabel,
  isOwn,
  authorIsInternal,
  isBeingEdited,
  editingDescription,
  editingCategories,
  editingInternalOnly,
  onChangeEditingDescription,
  onChangeEditingCategories,
  onChangeEditingInternalOnly,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  renderCategoryItem,
  labels,
  className,
}: Props) {
  const canSaveEdit = editingDescription.trim().length > 0 && editingCategories.length > 0;
  const isGbUser = useIsGbUser();

  return (
    <Card
      className={`overflow-hidden !rounded-md !border !border-gray-200 !shadow-none transition-colors ${isBeingEdited ? '!bg-gray-50' : '!bg-white hover:!bg-gray-50'} ${className ?? ''}`}
    >
      <CardBody className="!p-3">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">{header}</div>
          {isBeingEdited ? (
            <MultiSelect
              data={categories}
              textField="text"
              dataItemKey="value"
              value={editingCategories}
              onChange={(e: MultiSelectChangeEvent) => onChangeEditingCategories(e.value)}
              itemRender={renderCategoryItem}
              autoClose={false}
              popupSettings={{ className: 'my-multiselect-popup notes-drawer-popup' }}
              size="large"
              style={{ width: 260, overflow: 'auto', height: 38 }}
            />
          ) : (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
              {note.categories.map((cat) => (
                <Chip key={cat} text={categoryLabel(cat)} themeColor="base" style={{ pointerEvents: 'none' }} />
              ))}
            </div>
          )}
        </div>

        {isBeingEdited ? (
          <NoteDescriptionInput
            value={editingDescription}
            onChange={onChangeEditingDescription}
            rows={3}
            showInternalToggle={isGbUser}
            internalOnly={editingInternalOnly}
            onInternalOnlyChange={onChangeEditingInternalOnly}
          />
        ) : (
          <NoteText text={note.description} />
        )}

        <div className="mt-2 flex items-center justify-between gap-2 text-xs">
          <div className="flex min-w-0 items-center gap-1 opacity-70" title={labels.createdBy}>
            {authorIsInternal ? (
              <img src={greaseBossMark} alt="GreaseBoss" className="h-4 w-auto shrink-0 self-center" />
            ) : (
              <SvgIcon icon={userIcon} />
            )}
            <span className="truncate">{note.createdBy}</span>
          </div>
          {isBeingEdited ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button size="small" onClick={onCancelEdit}>
                {labels.cancel}
              </Button>
              <Button size="small" themeColor="primary" disabled={!canSaveEdit} onClick={onSaveEdit}>
                {labels.save}
              </Button>
            </div>
          ) : isOwn ? (
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                fillMode="flat"
                themeColor="base"
                size="small"
                svgIcon={pencilIcon}
                onClick={onStartEdit}
                title={labels.edit}
                aria-label={labels.edit}
              />
              <Button
                fillMode="flat"
                themeColor="base"
                size="small"
                svgIcon={trashIcon}
                onClick={onDelete}
                title={labels.delete}
                aria-label={labels.delete}
              />
            </div>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

/** Shared "Internal only" badge — a plain Kendo Chip shown next to the timestamp. */
export function InternalChip() {
  return (
    <span title="Internal only — visible to GreaseBoss users">
      <Chip text="Internal" themeColor="base" size="small" style={{ pointerEvents: 'none' }} />
    </span>
  );
}
